import React, { ChangeEvent, Reducer, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Button, IconButton, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useAuthContext } from 'AuthContext';
import reducer, {
  initialState,
  MeetingTableAction,
  MeetingTableActionType,
  MeetingTableState,
} from './MeetingTableReducer';
import SearchInput from 'components/shared/SearchInput';
import Table from 'components/shared/Table';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import EditMeetingModal from '../EditMeetingModal';
import DeleteMeetingModal from '../DeleteMeetingModal';
import ValidatePasswordModal from '../ValidatePasswordModal';
import routes from 'store/configs/Routes';
import { MeetingView } from 'store/types/Meeting';
import { SortConfig, TableColumn } from 'store/types/Table';
import { getFormattedDate } from 'util/format';

import styles from './MeetingTable.module.scss';

interface MeetingsTableProps {
  label?: string;
  data?: MeetingView[];
  loading?: boolean;
  refetch: () => void;
  defaultSort?: SortConfig<MeetingView>;
  allowCreate?: boolean;
  showPagination?: boolean;
}

const MeetingsTable: React.FunctionComponent<MeetingsTableProps> = ({
  label = 'Meetings',
  data = [],
  loading,
  refetch,
  defaultSort,
  allowCreate = false,
  showPagination = false,
}) => {
  const { userLoading, isStaff } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [{ list = [], sort, filter }, dispatch] = useReducer<Reducer<MeetingTableState, MeetingTableAction>>(
    reducer,
    initialState
  );
  const [editMeetingModalOpen, setEditMeetingModalOpen] = useState<boolean>(false);
  const [deleteMeetingModalOpen, setDeleteMeetingModalOpen] = useState<boolean>(false);
  const [validatePasswordModalOpen, setValidatePasswordModalOpen] = useState<boolean>(false);
  const [selectedMeeting, setSelectedMeeting] = useState<MeetingView | undefined>(undefined);

  const isLoading = useMemo(() => userLoading || loading, [loading, userLoading]);

  useEffect(() => {
    if (data) {
      dispatch({
        type: MeetingTableActionType.SetInitialList,
        payload: { initialList: data },
      });
    }
  }, [data]);

  useEffect(() => {
    if (defaultSort) {
      dispatch({
        type: MeetingTableActionType.UpdateSort,
        payload: { sort: defaultSort },
      });
    }
  }, [defaultSort]);

  const onSortChange = useCallback((newSort: SortConfig) => {
    dispatch({ type: MeetingTableActionType.UpdateSort, payload: { sort: newSort } });
  }, []);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: MeetingTableActionType.UpdateFilter,
      payload: { filter: { search: e.target.value } },
    });
  }, []);

  const handleCreateMeeting = useCallback(() => {
    setSelectedMeeting(undefined);
    setEditMeetingModalOpen(true);
  }, []);

  const handleEditMeeting = useCallback((meeting: MeetingView) => {
    setSelectedMeeting(meeting);
    setEditMeetingModalOpen(true);
  }, []);

  const handleEditMeetingModalClose = useCallback(() => {
    setEditMeetingModalOpen(false);
    setSelectedMeeting(undefined);
  }, []);

  const handleEditMeetingSubmit = useCallback(() => {
    handleEditMeetingModalClose();
    setSelectedMeeting(undefined);
    refetch();
  }, [handleEditMeetingModalClose, refetch]);

  const handleDeleteMeetingModalOpen = useCallback((meeting: MeetingView) => {
    setSelectedMeeting(meeting);
    setDeleteMeetingModalOpen(true);
  }, []);

  const handleDeleteMeetingModalClose = useCallback(() => {
    setDeleteMeetingModalOpen(false);
    setSelectedMeeting(undefined);
  }, []);

  const handleDeleteMeetingModalSubmit = useCallback(() => {
    setDeleteMeetingModalOpen(false);
    setSelectedMeeting(undefined);
    refetch();
  }, [refetch]);

  const handleJoinMeetingClick = useCallback(
    (meeting: MeetingView) => {
      if (isStaff) {
        setSelectedMeeting(meeting);
        setValidatePasswordModalOpen(true);
      } else {
        navigate(`${routes.meetings}/${meeting?.id}`);
      }
    },
    [isStaff, navigate]
  );

  const handleValidatePasswordModalClose = useCallback(() => {
    setValidatePasswordModalOpen(false);
    setSelectedMeeting(undefined);
  }, []);

  const handleJoinMeetingSubmit = useCallback(() => {
    enqueueSnackbar('Joined meeting', { variant: 'success' });
    navigate(`${routes.meetings}/${selectedMeeting?.id}`);
  }, [enqueueSnackbar, navigate, selectedMeeting?.id]);

  const columns: Array<TableColumn<MeetingView>> = useMemo(() => {
    return [
      {
        dataIndex: 'committees',
        label: <Typography fontWeight="bold">Committee</Typography>,
        render: (_, record) => <Typography>{record.committees.join(', ')}</Typography>,
      },
      {
        dataIndex: 'title',
        label: <Typography fontWeight="bold">Name</Typography>,
        sortable: true,
        render: (_, record) => <Typography>{record.title}</Typography>,
      },
      {
        dataIndex: 'region',
        label: <Typography fontWeight="bold">Region</Typography>,
        sortable: true,
        render: (_, record) => <Typography>{record.region}</Typography>,
      },
      {
        dataIndex: 'startDate',
        label: <Typography fontWeight="bold">Start Date</Typography>,
        sortable: true,
        render: (_, record) => <Typography>{getFormattedDate(record.startDate ?? '', 'L LT')}</Typography>,
      },
      {
        dataIndex: 'endDate',
        label: <Typography fontWeight="bold">End Date</Typography>,
        sortable: true,
        render: (_, record) => <Typography>{getFormattedDate(record.endDate ?? '', 'L LT')}</Typography>,
      },
      {
        dataIndex: 'timezone',
        label: <Typography fontWeight="bold">Time Zone</Typography>,
        render: (_, record) => <Typography>{record.timezone}</Typography>,
      },
      {
        key: 'actions',
        align: 'center',
        label: <Typography fontWeight="bold">Actions</Typography>,
        render: (_, record) => {
          return (
            <div className={styles.buttonGroup}>
              <Button color="primary" variant="contained" onClick={() => handleJoinMeetingClick(record)}>
                Join
              </Button>
              {isStaff && (
                <>
                  <IconButton color="primary" onClick={() => handleEditMeeting(record)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDeleteMeetingModalOpen(record)}>
                    <CloseIcon />
                  </IconButton>
                </>
              )}
            </div>
          );
        },
      },
    ];
  }, [handleDeleteMeetingModalOpen, handleEditMeeting, handleJoinMeetingClick, isStaff]);

  return (
    <>
      <GridContainer>
        <GridItem>
          <h2 className={styles.title}>{label}</h2>
        </GridItem>

        <GridItem sm={6}>
          <SearchInput value={filter.search} onChange={handleSearchChange} />
        </GridItem>

        {isStaff && allowCreate && (
          <GridItem sm={6} className="d-flex flex-row-reverse">
            <Button color={'primary'} variant={'contained'} onClick={handleCreateMeeting}>
              Create Meeting
            </Button>
          </GridItem>
        )}

        <GridItem>
          <Table
            columns={columns}
            list={list}
            sort={sort}
            onSortChange={onSortChange}
            loading={isLoading}
            noResultsText="No current meetings"
            showPagination={showPagination}
          />
        </GridItem>
      </GridContainer>
      <EditMeetingModal
        data={selectedMeeting}
        onClose={handleEditMeetingModalClose}
        open={editMeetingModalOpen}
        onSubmit={handleEditMeetingSubmit}
      />
      <DeleteMeetingModal
        data={selectedMeeting}
        onClose={handleDeleteMeetingModalClose}
        open={deleteMeetingModalOpen}
        onSubmit={handleDeleteMeetingModalSubmit}
      />
      <ValidatePasswordModal
        meetingId={selectedMeeting?.id}
        onClose={handleValidatePasswordModalClose}
        open={validatePasswordModalOpen}
        onSubmit={handleJoinMeetingSubmit}
      />
    </>
  );
};

export default MeetingsTable;
